'use client';

import { useCallback, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  useCheckoutStore,
  CHECKOUT_STEPS,
  selectBonusState,
  type CheckoutStep,
} from '@/stores/checkout-store';
import { useAuthStore, selectIsAuthenticated } from '@/stores/auth-store';
import { getRouletteConfig } from '@/lib/api/roulette';
import { getOrders } from '@/lib/api/orders';

/**
 * Какие шаги оформления показывать (вопрос 19, ответ «А»).
 *
 * Было: воронка жёстко из семи шагов, их проходили все. Выключенная рулетка
 * показывалась экраном «Рулетка сейчас недоступна» с кнопкой «Пропустить», а
 * постоянному клиенту после полутора секунд искусственной паузы показывали
 * КРАСНЫЙ блок «Вы уже получили подарок за первый заказ» — прямо перед кнопкой
 * «Оплатить». В приложении шаг рулетки при отключении уже пропускался сам,
 * то есть платформы вели себя по-разному.
 *
 * Стало: шаг, на котором человеку нечего дать, не показывается вовсе.
 *
 * Проверка «первый ли это заказ» переехала сюда из самого шага: чтобы шаг
 * пропустить, ответ нужен ДО того, как человек на него попал. Заодно исчезла
 * пауза setTimeout(1500) — она была написана «для лучшего UX», а на деле
 * задерживала показ пустого экрана.
 *
 * Порядок шагов и переходы живут в checkout-store; здесь только фильтр.
 */

/** Первый ли это заказ. Пишет ответ в checkout-store, считает один раз. */
function useFirstOrderCheck(enabled: boolean) {
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const bonusState = useCheckoutStore(selectBonusState);
  const setBonusState = useCheckoutStore((state) => state.setBonusState);

  useEffect(() => {
    if (!enabled || !isAuthenticated) return;
    if (bonusState.isFirstOrderChecked || bonusState.isCheckingFirstOrder) return;

    let cancelled = false;
    setBonusState({ isCheckingFirstOrder: true });

    (async () => {
      try {
        // Только оплаченные: неоплаченная попытка — не заказ, и лишать из-за
        // неё подарка нельзя. Ровно так же считает сервер при оформлении.
        const orders = await getOrders(false);
        if (cancelled) return;
        setBonusState({
          isFirstOrder: orders.length === 0,
          isFirstOrderChecked: true,
          isCheckingFirstOrder: false,
          firstOrderCheckSuccess: true,
        });
      } catch {
        if (cancelled) return;
        // Не смогли проверить — считаем, что подарок не положен (безопасный
        // умолчание: сервер всё равно пересоберёт бонусы сам).
        setBonusState({
          isFirstOrder: false,
          isFirstOrderChecked: true,
          isCheckingFirstOrder: false,
          firstOrderCheckSuccess: false,
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    enabled,
    isAuthenticated,
    bonusState.isFirstOrderChecked,
    bonusState.isCheckingFirstOrder,
    setBonusState,
  ]);
}

export interface VisibleCheckoutSteps {
  /** Шаги, которые человек реально увидит, в порядке прохождения. */
  steps: CheckoutStep[];
  /** Позиция текущего шага среди видимых (0-based). -1, если шаг скрыт. */
  index: number;
  /** Прогресс в процентах — по видимым шагам, а не по всем семи. */
  progress: number;
  /** Следующий/предыдущий видимый шаг или null, если дальше некуда. */
  nextVisibleStep: () => CheckoutStep | null;
  prevVisibleStep: () => CheckoutStep | null;
}

export function useVisibleCheckoutSteps(isOpen: boolean): VisibleCheckoutSteps {
  const currentStep = useCheckoutStore((state) => state.currentStep);
  const setStep = useCheckoutStore((state) => state.setStep);
  const bonusState = useCheckoutStore(selectBonusState);
  const isAuthenticated = useAuthStore(selectIsAuthenticated);

  useFirstOrderCheck(isOpen);

  // Тот же ключ и staleTime, что у RouletteStep — запрос один на оба места.
  const { data: rouletteConfig } = useQuery({
    queryKey: ['roulette-config'],
    queryFn: getRouletteConfig,
    staleTime: 60000,
    enabled: isOpen,
  });

  const steps = useMemo<CheckoutStep[]>(() => {
    // Пока конфиг не приехал — шаг показываем: спрятать и вернуть его обратно
    // хуже, чем показать один раз. Если окажется, что рулетка выключена,
    // эффект ниже уведёт с этого шага сам.
    const rouletteVisible = rouletteConfig
      ? rouletteConfig.is_active && rouletteConfig.products.length > 0
      : true;

    // Гостю шаг нужен: подарок ему ещё положен, там кнопка «Войти и получить».
    // Скрываем только у того, кто подарок уже забрал.
    const bonusVisible = !(
      isAuthenticated &&
      bonusState.isFirstOrderChecked &&
      !bonusState.isFirstOrder
    );

    return CHECKOUT_STEPS.filter((step) => {
      if (step === 'roulette') return rouletteVisible;
      if (step === 'bonus') return bonusVisible;
      return true;
    });
  }, [rouletteConfig, isAuthenticated, bonusState.isFirstOrderChecked, bonusState.isFirstOrder]);

  const index = steps.indexOf(currentStep);

  // Шаг стал невидимым, пока человек на нём стоял (рулетку выключили,
  // проверка первого заказа доехала) — уводим вперёд, а не оставляем в тупике.
  useEffect(() => {
    if (!isOpen) return;
    if (steps.includes(currentStep)) return;

    const orderIndex = CHECKOUT_STEPS.indexOf(currentStep);
    const forward = steps.find((step) => CHECKOUT_STEPS.indexOf(step) > orderIndex);
    if (forward) setStep(forward);
    else if (steps.length > 0) setStep(steps[steps.length - 1]);
  }, [isOpen, steps, currentStep, setStep]);

  const nextVisibleStep = useCallback((): CheckoutStep | null => {
    const orderIndex = CHECKOUT_STEPS.indexOf(currentStep);
    return steps.find((step) => CHECKOUT_STEPS.indexOf(step) > orderIndex) ?? null;
  }, [steps, currentStep]);

  const prevVisibleStep = useCallback((): CheckoutStep | null => {
    const orderIndex = CHECKOUT_STEPS.indexOf(currentStep);
    const earlier = steps.filter((step) => CHECKOUT_STEPS.indexOf(step) < orderIndex);
    return earlier.length > 0 ? earlier[earlier.length - 1] : null;
  }, [steps, currentStep]);

  const progress = steps.length > 0 ? ((Math.max(index, 0) + 1) / steps.length) * 100 : 0;

  // Стабильная ссылка: результат уходит в зависимости useCallback у обработчиков
  // корзины, и новый объект на каждый рендер пересоздавал бы их все.
  return useMemo(
    () => ({ steps, index, progress, nextVisibleStep, prevVisibleStep }),
    [steps, index, progress, nextVisibleStep, prevVisibleStep]
  );
}
